// src/components/Groups/SharedResourceCard.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Badge, OverlayTrigger, Tooltip, Form } from 'react-bootstrap';
import {
  FlaskConical, BarChart2, Image, FileText,
  Eye, Edit3, ShieldCheck, Trash2,
  ExternalLink
} from 'lucide-react';

const TYPE_ICONS = {
  experiment: <FlaskConical size={18} />,
  analysis: <BarChart2 size={18} />,
  visualization: <Image size={18} />,
  plateschema: <FileText size={18} />,
  photo_job: <Image size={18} />,
  default: <FileText size={18} />,
};

const PERM_META = {
  view: { icon: <Eye size={13} />, label: 'View only', variant: 'info' },
  edit: { icon: <Edit3 size={13} />, label: 'View & Edit', variant: 'warning' },
  admin: { icon: <ShieldCheck size={13} />, label: 'Full control', variant: 'danger' },
};

const SharedResourceCard = ({
  resource,
  canManage,
  onUnshare,
  onChangePermission,
}) => {
  const navigate = useNavigate();
  const [selectedPermission, setSelectedPermission] = useState(resource.permission_level);

  const type = resource.resource_type || 'default';
  const icon = TYPE_ICONS[type] || TYPE_ICONS.default;
  const perm = PERM_META[resource.permission_level] || PERM_META.view;

  const name =
    resource.resource_details?.name ||
    resource.resource_details?.original_image_filename ||
    resource.resource_details?.description ||
    `${type} #${resource.object_id?.slice(0, 8)}`;

  const getResourceUrl = () => {
    const objectId = resource.object_id;
    switch (type) {
      case 'experiment':
        return `/experiment/${objectId}`;
      case 'photoanalysisjob':
      case 'photo_job':
        return `/processing-results/${objectId}`;
      case 'analysis':
        return `/experiment/${resource.resource_details?.experiment_id || objectId}`;
      default:
        return null;
    }
  };

  const handleCardClick = () => {
    const url = getResourceUrl();
    if (url) {
      navigate(url);
    }
  };

  const handleUnshare = (e) => {
    e.stopPropagation();
    if (window.confirm('Remove this resource from the group?')) {
      onUnshare(resource.id);
    }
  };

  const handlePermissionChange = (e) => {
    e.stopPropagation();
    const newLevel = e.target.value;
    setSelectedPermission(newLevel);
    onChangePermission(resource.id, newLevel);
  };

  const resourceUrl = getResourceUrl();

  return (
    <Card
      className={`shared-resource-card mb-2 ${resourceUrl ? 'clickable' : ''}`}
      onClick={resourceUrl ? handleCardClick : undefined}
    >
      <Card.Body className="d-flex align-items-center gap-3 p-3">
        {/* Icon */}
        <div className="shared-resource-card__icon flex-shrink-0">
          {icon}
        </div>

        {/* Content */}
        <div className="flex-grow-1 min-width-0">
          <div className="d-flex align-items-center gap-2 mb-1">
            <span className="shared-resource-card__name fw-semibold text-truncate">
              {name}
            </span>
            {resourceUrl && (
              <ExternalLink size={12} className="shared-resource-card__link-icon text-muted flex-shrink-0" />
            )}
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <Badge bg="secondary" className="text-capitalize">
              {type}
            </Badge>
            <small className="text-muted">
              Shared by {resource.shared_by_details?.full_name || resource.shared_by_details?.email || resource.shared_by_email || 'Unknown'}
            </small>
          </div>
        </div>

        {/* Permission Badge and Actions */}
        <div className="d-flex align-items-center gap-2 flex-shrink-0">
          {canManage ? (
            <Form.Select
              size="sm"
              value={selectedPermission}
              onChange={handlePermissionChange}
              onClick={(e) => e.stopPropagation()}
              className="permission-select"
              style={{ width: '130px' }}
            >
              <option value="view">
                👁️ View only
              </option>
              <option value="edit">
                ✏️ View & Edit
              </option>
              <option value="admin">
                🛡️ Full control
              </option>
            </Form.Select>
          ) : (
            <Badge bg={perm.variant} className="d-flex align-items-center gap-1 px-2 py-1">
              {perm.icon}
              <span>{perm.label}</span>
            </Badge>
          )}

          {canManage && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Unshare</Tooltip>}
            >
              <Button
                variant="link"
                size="sm"
                className="icon-btn icon-btn--danger p-1"
                onClick={handleUnshare}
              >
                <Trash2 size={15} />
              </Button>
            </OverlayTrigger>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default SharedResourceCard;